// PDF export. Snapshots the live stage to a JPEG (same path as exportJpeg)
// then wraps it in a single-page PDF sized to the image's aspect ratio —
// no Letter/A4 letterboxing, the page IS the board.

import { jsPDF } from 'jspdf';
import type Konva from 'konva';
import type { Shape } from '@frame/shared';
import { snapshotStageRegion } from './snapshot';
import { exportFilename, triggerDownload } from './download';

export interface PdfExportOpts {
  pixelRatio?: number;
  quality?: number;
  background?: string;
}

const POINTS_PER_INCH = 72;
const DPI = 96;

export async function exportPdf(
  stage: Konva.Stage,
  shapes: readonly Shape[],
  boardId: string,
  opts: PdfExportOpts = {},
): Promise<void> {
  const canvas = await snapshotStageRegion(stage, shapes, {
    pixelRatio: opts.pixelRatio ?? 2,
    background: opts.background ?? '#ffffff',
  });
  const jpegDataUrl = canvas.toDataURL('image/jpeg', opts.quality ?? 0.92);

  const pixelRatio = opts.pixelRatio ?? 2;
  const cssWidth = canvas.width / pixelRatio;
  const cssHeight = canvas.height / pixelRatio;
  // Cap page dimensions at 200" so absurdly large boards don't generate
  // a PDF that PDF readers refuse to open.
  const widthPt = Math.min(cssWidth * (POINTS_PER_INCH / DPI), 14400);
  const heightPt = Math.min(cssHeight * (POINTS_PER_INCH / DPI), 14400);

  const orientation: 'portrait' | 'landscape' = widthPt >= heightPt ? 'landscape' : 'portrait';
  const pdf = new jsPDF({
    orientation,
    unit: 'pt',
    format: [widthPt, heightPt],
    compress: true,
  });
  pdf.addImage(jpegDataUrl, 'JPEG', 0, 0, widthPt, heightPt);
  const blob = pdf.output('blob');
  triggerDownload(blob, exportFilename(boardId, 'pdf'));
}
